import { v4 as generateUuid } from 'uuid'
import { eventChannel, END, buffers, channel } from '@redux-saga/core'
import { all, call, fork, put, putResolve, take } from '@redux-saga/core/effects'
import { resourcesActions } from '@/redux/actions'
import { UPLOAD_RESOURCES } from '@/redux/actions/resources.actions'
import { resourcesService } from '@/services'

function* createChannel(handler) {
	const queueChannel = yield call(channel, buffers.expanding())

	function* watcher() {
		const workersChannel = yield call(channel, buffers.expanding())

		yield fork(worker, workersChannel)

		while (true) {
			const { payload } = yield take(queueChannel)
			yield put(workersChannel, payload)
		}
	}

	function* worker(chan) {
		while (true) {
			const payload = yield take(chan)
			yield handler(payload)
		}
	}

	return { queueChannel, watcher }
}

function createEventChannel() {
	let emit

	const eChannel = eventChannel((emitter) => {
		emit = emitter
		return () => { }
	})
	return { emit, eChannel }
}

function createUploadPromise(requestUuid, domain, entity, file, emit) {
	const onProgress = ({ total, loaded }) => {
		const percentage = Math.round((loaded * 100) / total)
		emit({
			requestUuid,
			percentage
		})
	}

	const uploadPromise = () => {
		return resourcesService
			.uploadResource(domain, entity, file, onProgress)
			.then((res) => {
				if (res.status === 200) {
					emit(END)
					return res.data
				} else {
					emit(new Error(res))
					emit(END)
					Promise.reject(res)
				}
			})
			.catch((err) => {
				emit(new Error(err))
				emit(END)
				Promise.reject(err)
			})
	}

	return uploadPromise
}

function* uploadProgressWatcher(requestUuid, file, channel) {
	while (true) {
		try {
			const { requestUuid, percentage } = yield take(channel)
			yield put(resourcesActions.updateResourceUploadProgress(requestUuid, percentage))
		} catch (error) {
			yield put(resourcesActions.uploadResource.error(requestUuid, file, error))
		}
	}
}

function* uploadResource(props) {
	const { domain, entity, file, requestUuid } = props

	try {
		const { eChannel, emit } = createEventChannel()
		const uploadPromise = createUploadPromise(requestUuid, domain, entity, file, emit)
		yield fork(uploadProgressWatcher, requestUuid, file, eChannel)
		const response = yield call(uploadPromise)
		if (response) yield put(resourcesActions.uploadResource.success(requestUuid, response[0]))
	} catch (error) {
		yield put(resourcesActions.uploadResource.error(requestUuid, file, error))
	}
}

function* uploadResourcesFlow() {
	while (true) {
		const {
			payload: { domain, entity, files }
		} = yield take(UPLOAD_RESOURCES)
		const { queueChannel, watcher } = yield createChannel(uploadResource)
		yield fork(watcher)
		const enqueueAll = Array.from(files).map((file) => {
			const requestUuid = generateUuid()
			return putResolve(resourcesActions.enqueueFile(requestUuid, file.name))
		})
		const queueFiles = yield all(enqueueAll)
		yield all(
			queueFiles.map(({ payload }, index) =>
				put(queueChannel, {
					payload: { domain, entity, file: Array.from(files)[index], requestUuid: payload.requestUuid }
				})
			)
		)
	}
}

export default function* rootSaga() {
	yield all([uploadResourcesFlow()])
}
